import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  IconButton,
  Alert,
  Snackbar,
  Collapse,
  Stack,
  Paper,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Call,
  Mail,
  Map,
  Warning,
  Business,
  ContactPhone,
  LocalShipping,
  CalendarToday,
  Security,
  Assignment,
  ExpandLess,
  ExpandMore,
  CheckCircle,
  InfoRounded,
  TrendingUp,
  Send,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { adminApi } from '../../services/adminApi';


const BatchResultsView = ({ data, onQuickEmail }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };


  const getProgressPercentage = () => {
    if (!data.totalSearched) return 0;
    return Math.round((data.processed / data.totalSearched) * 100);
  };

  const errorCount = typeof data.errorCount === 'number' ? data.errorCount : (data.errors ? data.errors.length : 0);

  return (
    <>

      <Card sx={{ mb: 2, background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(16,185,129,0.1) 100%)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {data.isComplete ? '✅' : '🔄'} Batch Search {data.isComplete ? 'Complete' : 'In Progress'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Processed {data.processed || 0} of {data.totalSearched} carriers • Found {data.successful} valid results
                {errorCount > 0 && ` • ${errorCount} errors`}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h6" color="primary" fontWeight="bold">
                {getProgressPercentage()}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data.isComplete ? 'Complete' : 'Processing...'}
              </Typography>
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ width: '100%', height: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 4, overflow: 'hidden', mb: 2 }}>
            <Box
              sx={{
                height: '100%',
                background: data.isComplete ?
                  'linear-gradient(135deg, #10b981 0%, #059669 100%)' :
                  'linear-gradient(135deg, #6366f1 0%, #10b981 100%)',
                width: `${getProgressPercentage()}%`,
                transition: 'width 0.3s ease'
              }}
            />
          </Box>

          {/* Summary Chips */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`Total: ${data.totalSearched}`} color="primary" variant="outlined" />
            <Chip label={`Found: ${data.successful}`} color="success" />
            <Chip
              label={`Remaining: ${data.totalSearched - (data.processed || 0)}`}
              color={data.isComplete ? "default" : "warning"}
            />
            {errorCount > 0 && (
              <Chip label={`Not Found: ${errorCount}`} color="error" />
            )}
          </Box>
        </CardContent>
      </Card>


      <Box>
        <Typography variant="h6" gutterBottom sx={{
          color: data.isComplete ? '#10b981' : '#6366f1',
          mb: 2
        }}>
          {data.isComplete ? '✅' : '⏳'} Found Carriers ({data.successful})
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {data.batchResults && data.batchResults.map((carrier, index) => (
     
            <CarrierCard
              key={`carrier-${carrier.dotNumber || index}-${index}`}
              data={carrier}
            
              onToggleView={() => {}}
              onQuickEmail={onQuickEmail}
            />
          ))}

        
          {(!data.batchResults || data.batchResults.length === 0) && (
            <Card sx={{ textAlign: 'center', py: 4 }}>
              {data.isComplete ? (
                <>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Carriers Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No valid carriers found in the batch search
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    🔍 Searching...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Batch search is in progress. Results will appear here as they are found.
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      Currently processing: {data.processed || 0} of {data.totalSearched}
                    </Typography>
                  </Box>
                </>
              )}
            </Card>
          )}
        </Box>
      </Box>

      {/* Completion Message */}
      {data.isComplete && (
        <Card sx={{ mt: 2, background: 'rgba(16, 185, 129, 0.1)', border: '1px solid #10b981' }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body1" color="#10b981" fontWeight="bold">
              ✅ Batch Search Completed Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Found {data.successful} carriers out of {data.totalSearched}
            {errorCount > 0 && ` • ${errorCount} errors`}
          </Typography>
        </CardContent>
      </Card>
    )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
};

// Helper function to calculate active duration in years, months and days
const calculateActiveDuration = (activeDuration) => {
  if (!activeDuration || activeDuration === 'N/A') return 'N/A';
  
  // Extract years, months and days from active duration string
  const yearsMatch = activeDuration.match(/(\d+)\s*years?/);
  const monthsMatch = activeDuration.match(/(\d+)\s*months?/);
  const daysMatch = activeDuration.match(/(\d+)\s*days?/);
  
  let totalDays = 0;
  
  if (yearsMatch) {
    totalDays += parseInt(yearsMatch[1]) * 365;
  }
  
  if (monthsMatch) {
    totalDays += parseInt(monthsMatch[1]) * 30;
  }

  if (daysMatch) {
    totalDays += parseInt(daysMatch[1]);
  }
  
  // Convert total days to years, months, days
  if (totalDays > 0) {
    const years = Math.floor(totalDays / 365);
    const remainingDaysAfterYears = totalDays % 365;
    const months = Math.floor(remainingDaysAfterYears / 30);
    const days = remainingDaysAfterYears % 30;
    
    const parts = [];
    
    if (years > 0) {
      parts.push(`${years} year${years > 1 ? 's' : ''}`);
    }
    if (months > 0) {
      parts.push(`${months} month${months > 1 ? 's' : ''}`);
    }
    if (days > 0 || parts.length === 0) {
      parts.push(`${days} day${days > 1 ? 's' : ''}`);
    }
    
    return parts.join(' ');
  }
  
  return 'N/A';
};

const formatAuthorityFlag = (value, activeLabel = 'Active', inactiveLabel = 'Not Active') => {
  if (value === true) return activeLabel;
  if (value === false) return inactiveLabel;
  return 'N/A';
};




const CarrierCard = ({ data, showBasicInfo, onToggleView, onQuickEmail }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [localShowBasic, setLocalShowBasic] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [callStatus, setCallStatus] = useState({});
  const [emailStatus, setEmailStatus] = useState({});
  const [expandedCarriers, setExpandedCarriers] = useState({});
  const { isAuthenticated, token } = useAuth();
  const carrierActionStorageKey = useMemo(() => {
    const uniqueId = [
      data?.dotNumber,
      data?.mcNumber,
      data?.email,
      data?.phone,
      data?.legalName,
    ]
      .filter(Boolean)
      .join('|');
    return uniqueId ? `carrierCard:actions:${uniqueId}` : '';
  }, [data]);

  useEffect(() => {
    if (!carrierActionStorageKey) return;
    try {
      const raw = localStorage.getItem(carrierActionStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.callStatus && typeof parsed.callStatus === 'object') {
        setCallStatus(parsed.callStatus);
      }
      if (parsed?.emailStatus && typeof parsed.emailStatus === 'object') {
        setEmailStatus(parsed.emailStatus);
      }
    } catch (e) {
      // ignore local cache errors
    }
  }, [carrierActionStorageKey]);

  const persistActionState = (nextCallStatus, nextEmailStatus) => {
    if (!carrierActionStorageKey) return;
    try {
      localStorage.setItem(
        carrierActionStorageKey,
        JSON.stringify({
          callStatus: nextCallStatus,
          emailStatus: nextEmailStatus,
          updatedAt: Date.now(),
        })
      );
    } catch (e) {
      // ignore local cache errors
    }
  };

  // Decide effective showBasic mode:
  const effectiveShowBasic = typeof showBasicInfo === 'boolean' ? showBasicInfo : localShowBasic;
  const toggleLocalView = () => {
    if (typeof showBasicInfo === 'boolean') {
    
      if (onToggleView) onToggleView();
    } else {
      // internal per-card toggle
      setLocalShowBasic(prev => !prev);
    }
  };

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleCopy = (text, label) => {
    if (!text || text === 'N/A') return;
    try {
      navigator.clipboard.writeText(text);
      showSnackbar(`✓ ${label} copied!`);
    } catch (e) {
      showSnackbar('Copy failed');
    }
  };

  const logActivity = async (type, source, meta = {}) => {
    if (!isAuthenticated || !token) return;
    try {
      await adminApi.logActivity({ type, source, metadata: meta }, token);
    } catch (e) {
      // silent fail; activity logging should not block UI
    }
  };

  const handleAction = (type, value, label) => {
    if (!value || value === 'N/A') return;

    switch (type) {
      case 'call':
        setCallStatus(prev => {
          const next = { ...prev, [value]: 'calling' };
          persistActionState(next, emailStatus);
          return next;
        });
        showSnackbar(`Calling ${label}...`);
        const tel = value.replace(/\D/g, '');
        setTimeout(() => {
          window.open(`tel:${tel}`);
          setCallStatus(prev => {
            const next = { ...prev, [value]: 'called' };
            persistActionState(next, emailStatus);
            return next;
          });
          logActivity('call', 'carrier_card', { target: value });
        }, 300);
        break;
      case 'email':
        setEmailStatus(prev => {
          const next = { ...prev, [value]: 'sending' };
          persistActionState(callStatus, next);
          return next;
        });
        showSnackbar(`Opening email for ${label}...`);
        setTimeout(() => {
          window.open(`mailto:${value}`);
          setEmailStatus(prev => {
            const next = { ...prev, [value]: 'sent' };
            persistActionState(callStatus, next);
            return next;
          });
          logActivity('email_send', 'carrier_card', { target: value });
        }, 300);
        break;
      case 'map':
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}`, '_blank');
        showSnackbar(`Opening map for ${label}...`);
        break;
    }
  };

  const formatPhone = (phone) => {
    if (!phone || phone === 'N/A') return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  if (!data) {
    return (
      <Card sx={{ textAlign: 'center', py: 4, mb: 2 }}>
        <Typography variant="h6" color="text.secondary">
          Ready for search...
        </Typography>
      </Card>
    );
  }


  if (data.searchType === 'batch') {
    return <BatchResultsView data={data} onQuickEmail={onQuickEmail} />;
  }


  const activeDuration = calculateActiveDuration(data.activeDuration);
  const carrierState = data.phyState && data.phyState !== 'N/A' ? data.phyState : data.mailState;
  const ownedBoxTrucks = Number.parseInt(data.ownTrucks, 10);
  const leasedBoxTrucks = Number.parseInt(data.termLeasedTrucks, 10);
  const hasOwnedBoxTruckValue = Number.isFinite(ownedBoxTrucks);
  const hasLeasedBoxTruckValue = Number.isFinite(leasedBoxTrucks);
  const totalBoxTrucks = (hasOwnedBoxTruckValue ? ownedBoxTrucks : 0) + (hasLeasedBoxTruckValue ? leasedBoxTrucks : 0);
  const showBoxTruckSummary = hasOwnedBoxTruckValue || hasLeasedBoxTruckValue;
  const panelStatus = data.authorityInfo?.commonAuthority || data.status || 'N/A';
  const panelStatusIsActive = panelStatus === 'Active';


if (effectiveShowBasic) {
  return (
    <>
      <Card sx={{
        background: 'linear-gradient(135deg, rgba(102,126,234,0.05) 0%, rgba(16,185,129,0.05) 100%)',
        border: '1px solid',
        borderColor: 'primary.main',
        borderRadius: 2,
        mb: 1.5,
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 8px 28px rgba(102,126,234,0.15)',
          transform: 'translateY(-2px)',
          borderColor: 'primary.light'
        }
      }}>
        <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1, sm: 1.5 } } }}>
          <Grid container spacing={1.5} alignItems="stretch">
            {/* Left Content - Main Info */}
            <Grid item xs={12} sm={8}>
              {/* Company Name & Status Section */}
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  mb: 1,
                  flexWrap: isMobile ? 'wrap' : 'nowrap'
                }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    flexShrink: 0
                  }}>
                    <Business sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        wordBreak: 'break-word',
                        mb: 0.25,
                        cursor: 'pointer',
                        transition: 'color 0.2s ease',
                        '&:hover': { color: 'primary.main' }
                      }}
                      onClick={() => handleCopy(data.dbaName !== 'N/A' ? data.dbaName : data.legalName, 'Carrier Name')}
                    >
                      {data.dbaName !== 'N/A' ? data.dbaName : data.legalName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      {data.companyOfficer1 && data.companyOfficer1 !== 'N/A' ? data.companyOfficer1 : ''}
                    </Typography>
                  </Box>
                </Box>

                {/* Status & Key Identifiers */}
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.25 }}>
                  {data.entityType && data.entityType !== 'N/A' && (
                    <Chip
                      icon={<LocalShipping />}
                      label={data.entityType}
                      variant="filled"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        background: data.entityType.includes('Carrier') ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                        color: data.entityType.includes('Carrier') ? '#059669' : '#dc2626',
                        '& .MuiChip-icon': {
                          color: 'inherit !important'
                        }
                      }}
                    />
                  )}
                  <Chip
                    icon={panelStatusIsActive ? <CheckCircle /> : <InfoRounded />}
                    label={panelStatus}
                    color={panelStatusIsActive ? 'success' : 'error'}
                    variant="filled"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      '& .MuiChip-icon': {
                        color: 'inherit !important'
                      }
                    }}
                  />
                  <Chip
                    icon={<Assignment />}
                    label={`DOT: ${data.dotNumber}`}
                    variant="outlined"
                    onClick={() => handleCopy(data.dotNumber, 'DOT')}
                    sx={{
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(102,126,234,0.08)',
                        borderColor: 'primary.dark'
                      }
                    }}
                  />
                  {data.mcNumber !== 'N/A' && (
                    <Chip
                      label={`MC: ${data.mcNumber}`}
                      variant="outlined"
                      onClick={() => handleCopy(data.mcNumber, 'MC')}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        borderColor: '#10b981',
                        color: '#10b981',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: 'rgba(16,185,129,0.08)',
                          borderColor: '#059669'
                        }
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Quick Info Row */}
              <Stack
                direction="row"
                spacing={1.5}
                useFlexGap
                flexWrap="wrap"
                sx={{ alignItems: 'center', fontSize: '0.7rem' }}
              >
                {data.phone && data.phone !== 'N/A' && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      cursor: 'pointer',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.75,
                      '&:hover': { background: 'rgba(0,0,0,0.04)' }
                    }}
                    onClick={() => handleCopy(formatPhone(data.phone), 'Phone')}
                  >
                    <Call sx={{ fontSize: 12, color: '#f59e0b' }} />
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.7rem', color: 'text.primary' }}
                    >
                      <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
                        Phone:
                      </Box>
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {formatPhone(data.phone)}
                      </Box>
                    </Typography>
                  </Stack>
                )}
                {data.email && data.email !== 'N/A' && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      cursor: 'pointer',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.75,
                      '&:hover': { background: 'rgba(0,0,0,0.04)' }
                    }}
                    onClick={() => handleCopy(data.email, 'Email')}
                  >
                    <Mail sx={{ fontSize: 12, color: '#ef4444' }} />
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.7rem', color: 'text.primary' }}
                    >
                      <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
                        Email:
                      </Box>
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {data.email}
                      </Box>
                    </Typography>
                  </Stack>
                )}
                {data.powerUnits && data.powerUnits !== 'N/A' && data.powerUnits !== '0' && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      cursor: 'pointer',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.75,
                      '&:hover': { background: 'rgba(0,0,0,0.04)' }
                    }}
                    onClick={() => handleCopy(`Power Units: ${data.powerUnits}`, 'Power Units')}
                  >
                    <TrendingUp sx={{ fontSize: 12, color: '#06b6d4' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                      <Box component="span" sx={{ color: 'text.secondary', mr: 0.5, fontWeight: 500 }}>
                        Power Units:
                      </Box>
                      {data.powerUnits}
                    </Typography>
                  </Stack>
                )}
                {showBoxTruckSummary && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      cursor: 'pointer',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.75,
                      '&:hover': { background: 'rgba(0,0,0,0.04)' }
                    }}
                    onClick={() => handleCopy(`Box Truck: ${totalBoxTrucks}`, 'Box Truck')}
                  >
                    <LocalShipping sx={{ fontSize: 12, color: '#f97316' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                      <Box component="span" sx={{ color: 'text.secondary', mr: 0.5, fontWeight: 500 }}>
                        Box Truck:
                      </Box>
                      {totalBoxTrucks}
                    </Typography>
                  </Stack>
                )}
                {carrierState && carrierState !== 'N/A' && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      cursor: 'pointer',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.75,
                      '&:hover': { background: 'rgba(0,0,0,0.04)' }
                    }}
                    onClick={() => handleCopy(carrierState, 'State')}
                  >
                    <Map sx={{ fontSize: 12, color: '#14b8a6' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                      <Box component="span" sx={{ color: 'text.secondary', mr: 0.5, fontWeight: 500 }}>
                        State:
                      </Box>
                      {carrierState}
                    </Typography>
                  </Stack>
                )}
                {data.carrierOperation && data.carrierOperation !== 'N/A' && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      cursor: 'pointer',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.75,
                      '&:hover': { background: 'rgba(0,0,0,0.04)' }
                    }}
                    onClick={() => handleCopy(data.carrierOperation, 'Carrier Type')}
                  >
                    <LocalShipping sx={{ fontSize: 12, color: '#8b5cf6' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                      <Box component="span" sx={{ color: 'text.secondary', mr: 0.5, fontWeight: 500 }}>
                        Type:
                      </Box>
                      {data.carrierOperation}
                    </Typography>
                  </Stack>
                )}
                {data.entityClassification && data.entityClassification !== 'N/A' && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      cursor: 'pointer',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.75,
                      '&:hover': { background: 'rgba(0,0,0,0.04)' }
                    }}
                    onClick={() => handleCopy(data.entityClassification, 'Operation Classification')}
                  >
                    <Assignment sx={{ fontSize: 12, color: '#3b82f6' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                      <Box component="span" sx={{ color: 'text.secondary', mr: 0.5, fontWeight: 500 }}>
                        Class:
                      </Box>
                      {data.entityClassification}
                    </Typography>
                  </Stack>
                )}
                {data.authorityInfo?.commonAuthority && data.authorityInfo.commonAuthority !== 'N/A' && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      cursor: 'pointer',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.75,
                      '&:hover': { background: 'rgba(0,0,0,0.04)' }
                    }}
                    onClick={() => handleCopy(data.authorityInfo.commonAuthority, 'Common Authority')}
                  >
                    <Security sx={{ fontSize: 12, color: data.authorityInfo.commonAuthority === 'Active' ? '#10b981' : '#ef4444' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                      <Box component="span" sx={{ color: 'text.secondary', mr: 0.5, fontWeight: 500 }}>
                        Authority:
                      </Box>
                      {data.authorityInfo.commonAuthority}
                    </Typography>
                  </Stack>
                )}
                {data.mcs150Mileage && data.mcs150Mileage !== 'N/A miles' && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      cursor: 'pointer',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.75,
                      '&:hover': { background: 'rgba(0,0,0,0.04)' }
                    }}
                    onClick={() => handleCopy(`${data.mcs150Mileage} (${data.mcs150MileageYear})`, 'MCS-150 Mileage')}
                  >
                    <TrendingUp sx={{ fontSize: 12, color: '#0ea5e9' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                      <Box component="span" sx={{ color: 'text.secondary', mr: 0.5, fontWeight: 500 }}>
                        Mileage:
                      </Box>
                      {data.mcs150Mileage}
                      {data.mcs150MileageYear && data.mcs150MileageYear !== 'N/A' ? ` (${data.mcs150MileageYear})` : ''}
                    </Typography>
                  </Stack>
                )}
                {activeDuration && activeDuration !== 'N/A' && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      cursor: 'pointer',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.75,
                      '&:hover': { background: 'rgba(0,0,0,0.04)' }
                    }}
                    onClick={() => handleCopy(activeDuration, 'Active Duration')}
                  >
                    <CalendarToday sx={{ fontSize: 12, color: '#6366f1' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                      <Box component="span" sx={{ color: 'text.secondary', mr: 0.5, fontWeight: 500 }}>
                        Active:
                      </Box>
                      {activeDuration}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Grid>

            {/* Right - Action Buttons */}
            <Grid item xs={12} sm={4} sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              justifyContent: 'space-between'
            }}>
              <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                <IconButton
                  size="small"
                  onClick={() => handleAction('call', data.phone, data.dbaName !== 'N/A' ? data.dbaName : data.legalName)}
                  disabled={!data.phone || data.phone === 'N/A'}
                  sx={{
                    background: callStatus[data.phone] === 'called' ? '#10b981' : 'rgba(16,185,129,0.1)',
                    color: callStatus[data.phone] === 'called' ? '#fff' : '#10b981',
                    width: 40,
                    height: 40,
                    transition: 'all 0.2s ease',
                    border: callStatus[data.phone] === 'called' ? '1.5px solid #059669' : '1.5px solid rgba(16,185,129,0.3)',
                    boxShadow: callStatus[data.phone] === 'called' ? '0 4px 12px rgba(16,185,129,0.35)' : 'none',
                    '&:hover:not(:disabled)': {
                      background: '#10b981',
                      color: 'white',
                      transform: 'scale(1.1)',
                      boxShadow: '0 4px 12px rgba(16,185,129,0.4)'
                    },
                    '&:disabled': {
                      opacity: 0.3
                    }
                  }}
                  title="Call carrier"
                >
                  {callStatus[data.phone] === 'called' ? <CheckCircle fontSize="small" /> : <Call fontSize="small" />}
                </IconButton>

                <IconButton
                  size="small"
                  onClick={() => handleAction('email', data.email, data.dbaName !== 'N/A' ? data.dbaName : data.legalName)}
                  disabled={!data.email || data.email === 'N/A'}
                  sx={{
                    background: emailStatus[data.email] === 'sent' ? '#6366f1' : 'rgba(99,102,241,0.1)',
                    color: emailStatus[data.email] === 'sent' ? '#fff' : '#6366f1',
                    width: 40,
                    height: 40,
                    transition: 'all 0.2s ease',
                    border: emailStatus[data.email] === 'sent' ? '1.5px solid #4f46e5' : '1.5px solid rgba(99,102,241,0.3)',
                    boxShadow: emailStatus[data.email] === 'sent' ? '0 4px 12px rgba(99,102,241,0.35)' : 'none',
                    '&:hover:not(:disabled)': {
                      background: '#6366f1',
                      color: 'white',
                      transform: 'scale(1.1)',
                      boxShadow: '0 4px 12px rgba(99,102,241,0.4)'
                    },
                    '&:disabled': {
                      opacity: 0.3
                    }
                  }}
                  title="Email carrier"
                >
                  {emailStatus[data.email] === 'sent' ? <CheckCircle fontSize="small" /> : <Mail fontSize="small" />}
                </IconButton>

                {/* Quick Email via Email Studio */}
                {onQuickEmail && data.email && data.email !== 'N/A' && (
                  <Tooltip title="Send via Email Studio" arrow>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEmailStatus(prev => {
                          const next = { ...prev, [data.email]: 'sent' };
                          persistActionState(callStatus, next);
                          return next;
                        });
                        onQuickEmail(data.email, data.dbaName !== 'N/A' ? data.dbaName : data.legalName, data);
                      }}
                      sx={{
                        background: emailStatus[data.email] === 'sent'
                          ? 'linear-gradient(135deg, #6366f1, #764ba2)'
                          : 'linear-gradient(135deg, rgba(102,126,234,0.15), rgba(118,75,162,0.15))',
                        color: emailStatus[data.email] === 'sent' ? '#fff' : '#764ba2',
                        width: 40,
                        height: 40,
                        transition: 'all 0.2s ease',
                        border: emailStatus[data.email] === 'sent'
                          ? '1.5px solid #4f46e5'
                          : '1.5px solid rgba(118,75,162,0.3)',
                        boxShadow: emailStatus[data.email] === 'sent' ? '0 4px 12px rgba(99,102,241,0.35)' : 'none',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #667eea, #764ba2)',
                          color: 'white',
                          transform: 'scale(1.1)',
                          boxShadow: '0 4px 12px rgba(118,75,162,0.4)',
                        },
                      }}
                    >
                      {emailStatus[data.email] === 'sent' ? <CheckCircle fontSize="small" /> : <Send fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                )}

                {data.physicalAddress && data.physicalAddress !== 'N/A' && (
                  <IconButton
                    size="small"
                    onClick={() => handleAction('map', data.physicalAddress, 'location')}
                    sx={{
                      background: 'rgba(236, 72, 153, 0.1)',
                      color: '#ec4899',
                      width: 32,
                      height: 32,
                      transition: 'all 0.2s ease',
                      border: '1px solid rgba(236, 72, 153, 0.3)',
                      '&:hover': {
                        background: '#ec4899',
                        color: 'white',
                        transform: 'scale(1.05)',
                        boxShadow: '0 3px 8px rgba(236, 72, 153, 0.35)'
                      }
                    }}
                    title="View on map"
                  >
                    <Map fontSize="inherit" />
                  </IconButton>
                )}
              </Stack>

              <Button
                variant="contained"
                size="small"
                onClick={toggleLocalView}
                endIcon={<ExpandMore />}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  transition: 'all 0.2s ease',
                  py: 0.6,
                  fontSize: '0.78rem',
                  width: { xs: '100%', sm: '33%' },
                  alignSelf: 'flex-end',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(102,126,234,0.35)'
                  }
                }}
              >
                {isMobile ? 'Details' : 'Full Details'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}


  return (
    <Box>
      {/* Header Card */}
      <Card sx={{ mb: 2, background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(16,185,129,0.1) 100%)' }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={8}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#6366f1' }}>
                {data.dbaName !== 'N/A' ? data.dbaName : data.legalName}
              </Typography>
              {/* Primary Contact in Header */}
              {data.companyOfficer1 && data.companyOfficer1 !== 'N/A' && (
                <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                  Primary Contact: {data.companyOfficer1}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={4} sx={{ textAlign: { sm: 'right' } }}>
              <Button
                variant="outlined"
                size="small"
                onClick={toggleLocalView}
                sx={{ fontSize: '0.75rem' }}
              >
                Back to Basic
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Alerts Card */}
      {data.alerts && data.alerts.length > 0 && (
        <Card sx={{ mb: 2, border: '2px solid #f59e0b', background: 'rgba(245, 158, 11, 0.1)' }}>
          <CardContent sx={{ p: 1.5 }}>
            <Alert severity="warning" icon={<Warning />} sx={{ mb: 0 }}>
              <Box sx={{ maxHeight: '80px', overflowY: 'auto' }}>
                {data.alerts.map((alert, index) => (
                  <Typography key={index} variant="body2" fontSize="0.75rem">
                    • {alert}
                  </Typography>
                ))}
              </Box>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Main Content - Cards with internal 2 columns */}
      <Grid container spacing={1}>

        {/* Company & Contact Information Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Business sx={{ color: '#6366f1', fontSize: '1.2rem' }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }} fontSize="0.9rem">
                  Company & Contact Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* Left Column */}
                <Grid item xs={6}>
                  <InfoItem label="Legal Name" value={data.legalName} onCopy={handleCopy} />
                  <InfoItem label="DBA Name" value={data.dbaName} onCopy={handleCopy} />
                  <InfoItem label="USDOT Number" value={data.dotNumber} onCopy={handleCopy} />
                  <InfoItem label="Docket" value={data.mcNumber !== 'N/A' ? `MC${data.mcNumber}` : 'N/A'} onCopy={handleCopy} />
                  <InfoItem label="Entity Type" value={data.entityType} onCopy={handleCopy} />
                  <InfoItem label="Business Type" value={data.businessOrganization} onCopy={handleCopy} />
                </Grid>

                {/* Right Column */}
                <Grid item xs={6}>
                  <InfoItem label="Primary Contact" value={data.companyOfficer1} onCopy={handleCopy} />
                  <InfoItem label="Secondary Contact" value={data.companyOfficer2} onCopy={handleCopy} />
                  <InfoItem
                    label="Email"
                    value={data.email}
                    onCopy={handleCopy}
                    actionable
                    actionType="email"
                    onAction={handleAction}
                    actionLabel={data.dbaName !== 'N/A' ? data.dbaName : data.legalName}
                  />
                  <InfoItem
                    label="Phone"
                    value={formatPhone(data.phone)}
                    onCopy={handleCopy}
                    actionable
                    actionType="call"
                    onAction={handleAction}
                    actionLabel={data.dbaName !== 'N/A' ? data.dbaName : data.legalName}
                  />
                  <InfoItem label="Fax" value={formatPhone(data.fax)} onCopy={handleCopy} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Operation & Status Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <LocalShipping sx={{ color: '#10b981', fontSize: '1.2rem' }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }} fontSize="0.9rem">
                  Operation & Status
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* Left Column */}
                <Grid item xs={6}>
                  <InfoItem label="Carrier Operation" value={data.carrierOperation} onCopy={handleCopy} />
                  <InfoItem label="Common Authority Status" value={panelStatus} status={panelStatusIsActive ? 'success' : 'error'} onCopy={handleCopy} />
                  <InfoItem label="Operation Classification" value={data.entityClassification} onCopy={handleCopy} />
                  <InfoItem label="Active Duration" value={activeDuration} onCopy={handleCopy} />
                </Grid>

                {/* Right Column */}
                <Grid item xs={6}>
                  <InfoItem label="USDOT Status" value={data.status} status={data.status === 'Active' ? 'success' : 'error'} onCopy={handleCopy} />
                  <InfoItem label="MCS-150 Date" value={data.mcs150Date} onCopy={handleCopy} />
                  <InfoItem label="Add Date" value={data.addDate} onCopy={handleCopy} />
                  <InfoItem label="Mileage Year" value={data.mcs150MileageYear} onCopy={handleCopy} />
                  <InfoItem label="MCS-150 Mileage" value={data.mcs150Mileage} onCopy={handleCopy} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Fleet Information Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <LocalShipping sx={{ color: '#f59e0b', fontSize: '1.2rem' }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }} fontSize="0.9rem">
                  Fleet Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* Left Column */}
                <Grid item xs={6}>
                  <InfoItem label="Total Drivers" value={data.totalDrivers} onCopy={handleCopy} />
                  <InfoItem label="CDL Drivers" value={data.totalCDLDrivers} onCopy={handleCopy} />
                  <InfoItem label="Power Units" value={data.powerUnits} onCopy={handleCopy} />
                </Grid>

                {/* Right Column - Vehicle Breakdown */}
                <Grid item xs={6}>
                  <Typography variant="body2" fontSize="0.75rem" fontWeight="bold" gutterBottom>
                    Vehicle Breakdown:
                  </Typography>

                  {/* Owned Vehicles */}
                  <Box sx={{ mb: 1.5, p: 1, background: 'rgba(16, 185, 129, 0.1)', borderRadius: 1 }}>
                    <Typography variant="body2" fontSize="0.7rem" fontWeight="bold" color="#10b981" gutterBottom>
                      🚛 Owned Vehicles:
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" fontSize="0.65rem" color="text.secondary">Tractors:</Typography>
                        <Typography variant="body2" fontSize="0.7rem" fontWeight="bold">{data.ownTractors}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" fontSize="0.65rem" color="text.secondary">Trailers:</Typography>
                        <Typography variant="body2" fontSize="0.7rem" fontWeight="bold">{data.ownTrailers}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" fontSize="0.65rem" color="text.secondary">Box Truck:</Typography>
                        <Typography variant="body2" fontSize="0.7rem" fontWeight="bold">{data.ownTrucks}</Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Leased Vehicles */}
                  <Box sx={{ p: 1, background: 'rgba(99, 102, 241, 0.1)', borderRadius: 1 }}>
                    <Typography variant="body2" fontSize="0.7rem" fontWeight="bold" color="#6366f1" gutterBottom>
                      📋 Leased Vehicles:
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" fontSize="0.65rem" color="text.secondary">Tractors:</Typography>
                        <Typography variant="body2" fontSize="0.7rem" fontWeight="bold">{data.termLeasedTractors}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" fontSize="0.65rem" color="text.secondary">Trailers:</Typography>
                        <Typography variant="body2" fontSize="0.7rem" fontWeight="bold">{data.termLeasedTrailers}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" fontSize="0.65rem" color="text.secondary">Box Truck:</Typography>
                        <Typography variant="body2" fontSize="0.7rem" fontWeight="bold">{data.termLeasedTrucks}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Address & Cargo Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <ContactPhone sx={{ color: '#8b5cf6', fontSize: '1.2rem' }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }} fontSize="0.9rem">
                  Address & Cargo
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* Left Column */}
                <Grid item xs={6}>
                  <InfoItem
                    label="Physical Address"
                    value={data.physicalAddress}
                    onCopy={handleCopy}
                    actionable
                    actionType="map"
                    onAction={handleAction}
                    actionLabel="Physical Address"
                  />
                  <InfoItem
                    label="Mailing Address"
                    value={data.mailingAddress !== data.physicalAddress ? data.mailingAddress : 'Same as Physical'}
                    onCopy={handleCopy}
                    actionable
                    actionType="map"
                    onAction={handleAction}
                    actionLabel="Mailing Address"
                  />
                  <InfoItem label="Cell Phone" value={formatPhone(data.cellPhone)} onCopy={handleCopy} />
                </Grid>

                {/* Right Column */}
                <Grid item xs={6}>
                  <InfoItem label="Cargo Classification" value={data.cargoClassification} onCopy={handleCopy} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Authority & Insurance Cards */}
        {data.authorityInfo && (
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Security sx={{ color: '#ef4444', fontSize: '1.2rem' }} />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }} fontSize="0.9rem">
                    Authority Information
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <InfoItem
                      label="Common Authority"
                      value={data.authorityInfo.commonAuthority}
                      status={data.authorityInfo.commonAuthority === 'Active' ? 'success' : 'error'}
                      onCopy={handleCopy}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoItem
                      label="Broker Authority"
                      value={data.authorityInfo.brokerAuthority}
                      status={data.authorityInfo.brokerAuthority === 'Active' ? 'success' : 'warning'}
                      onCopy={handleCopy}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                      <Chip
                        size="small"
                        label={`Pending Common: ${formatAuthorityFlag(data.authorityInfo.pendingCommon, 'Yes', 'No')}`}
                        color={data.authorityInfo.pendingCommon ? 'warning' : 'default'}
                        variant={data.authorityInfo.pendingCommon ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.65rem' }}
                      />
                      <Chip
                        size="small"
                        label={`Pending Contract: ${formatAuthorityFlag(data.authorityInfo.pendingContract, 'Yes', 'No')}`}
                        color={data.authorityInfo.pendingContract ? 'warning' : 'default'}
                        variant={data.authorityInfo.pendingContract ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.65rem' }}
                      />
                      <Chip
                        size="small"
                        label={`Pending Broker: ${formatAuthorityFlag(data.authorityInfo.pendingBroker, 'Yes', 'No')}`}
                        color={data.authorityInfo.pendingBroker ? 'warning' : 'default'}
                        variant={data.authorityInfo.pendingBroker ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.65rem' }}
                      />
                      <Chip
                        size="small"
                        label={`Common Revocation: ${formatAuthorityFlag(data.authorityInfo.revocationCommon, 'Yes', 'No')}`}
                        color={data.authorityInfo.revocationCommon ? 'error' : 'default'}
                        variant={data.authorityInfo.revocationCommon ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.65rem' }}
                      />
                      <Chip
                        size="small"
                        label={`Contract Revocation: ${formatAuthorityFlag(data.authorityInfo.revocationContract, 'Yes', 'No')}`}
                        color={data.authorityInfo.revocationContract ? 'error' : 'default'}
                        variant={data.authorityInfo.revocationContract ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.65rem' }}
                      />
                      <Chip
                        size="small"
                        label={`Broker Revocation: ${formatAuthorityFlag(data.authorityInfo.revocationBroker, 'Yes', 'No')}`}
                        color={data.authorityInfo.revocationBroker ? 'error' : 'default'}
                        variant={data.authorityInfo.revocationBroker ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.65rem' }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {data.insuranceInfo && (
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Assignment sx={{ color: '#06b6d4', fontSize: '1.2rem' }} />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }} fontSize="0.9rem">
                    Insurance Information
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <InfoItem label="Insurance Type" value={data.insuranceInfo.type} onCopy={handleCopy} />
                    <InfoItem label="BI&PD Class" value={data.insuranceInfo.class} onCopy={handleCopy} />
                    <InfoItem label="Policy Number" value={data.insuranceInfo.policyNumber} onCopy={handleCopy} />
                    <InfoItem label="Effective Date" value={data.insuranceInfo.effectiveDate} onCopy={handleCopy} />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoItem label="Coverage Amount" value={data.insuranceInfo.maxCoverage} onCopy={handleCopy} />
                    <InfoItem label="Underlying Limit" value={data.insuranceInfo.underlyingLimit} onCopy={handleCopy} />
                    <InfoItem label="Form Code" value={data.insuranceInfo.formCode} onCopy={handleCopy} />
                    <InfoItem label="Insurance Company" value={data.insuranceInfo.companyName} onCopy={handleCopy} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {data.pendingInsurance && (
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Warning sx={{ color: '#f59e0b', fontSize: '1.2rem' }} />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }} fontSize="0.9rem">
                    Pending Insurance
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <InfoItem label="Form Code" value={data.pendingInsurance.formCode} onCopy={handleCopy} />
                    <InfoItem label="Type Description" value={data.pendingInsurance.typeDescription} onCopy={handleCopy} />
                    <InfoItem label="Policy Number" value={data.pendingInsurance.policyNumber} onCopy={handleCopy} />
                    <InfoItem label="Posted Date" value={data.pendingInsurance.postedDate} onCopy={handleCopy} />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoItem label="Underlying Limit" value={data.pendingInsurance.underlyingLimit} onCopy={handleCopy} />
                    <InfoItem label="Maximum Limit" value={data.pendingInsurance.maxCoverage} onCopy={handleCopy} />
                    <InfoItem label="Effective Date" value={data.pendingInsurance.effectiveDate} onCopy={handleCopy} />
                    <InfoItem label="Cancel Effective Date" value={data.pendingInsurance.cancelEffectiveDate} onCopy={handleCopy} />
                    <InfoItem label="Insurance Company" value={data.pendingInsurance.companyName} onCopy={handleCopy} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {(data.authorityHistory || (activeDuration && activeDuration !== 'N/A')) && (
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <CalendarToday sx={{ color: '#6366f1', fontSize: '1.2rem' }} />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }} fontSize="0.9rem">
                    Authority History
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <InfoItem label="Active Duration" value={activeDuration} onCopy={handleCopy} />
                    <InfoItem label="Latest Action" value={data.authorityHistory?.action || 'N/A'} onCopy={handleCopy} />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoItem label="Service Type" value={data.authorityHistory?.serviceType || 'N/A'} onCopy={handleCopy} />
                    <InfoItem label="Served Date" value={data.authorityHistory?.servedDate || 'N/A'} onCopy={handleCopy} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* External Links Card */}
      <Card sx={{ mt: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom fontSize="0.8rem">
            Additional Resources
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.open(`https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=USDOT&query_string=${data.dotNumber}`, '_blank')}
              sx={{ fontSize: '0.7rem' }}
            >
              SAFER Snapshot
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.open(`https://li-public.fmcsa.dot.gov/LIVIEW/pkg_carrquery.prc_carrlist?cos_seq_no=${data.dotNumber}`, '_blank')}
              sx={{ fontSize: '0.7rem' }}
            >
              Licensing & Insurance
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

// Info Item Component
const InfoItem = ({ label, value, status, actionable, actionType, actionLabel, onCopy, onAction }) => (
  <Box sx={{ mb: 1.2 }}>
    <Typography variant="body2" color="text.secondary" fontSize="0.7rem" gutterBottom>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {status ? (
        <Chip
          label={value}
          color={status}
          size="small"
          variant="filled"
          sx={{ height: 20, fontSize: '0.6rem' }}
        />
      ) : (
        <Typography
          variant="body2"
          fontSize="0.75rem"
          fontWeight="bold"
          onClick={() => value && value !== 'N/A' && onCopy(value, label)}
          sx={{
            cursor: value && value !== 'N/A' ? 'pointer' : 'default',
            '&:hover': value && value !== 'N/A' ? { color: '#6366f1' } : {}
          }}
        >
          {value || 'N/A'}
        </Typography>
      )}
      {value && value !== 'N/A' && (
        <>
          {actionable && (
            <IconButton
              size="small"
              onClick={() => onAction(actionType, value, actionLabel)}
              sx={{
                width: 16,
                height: 16,
                color: actionType === 'call' ? '#10b981' : actionType === 'email' ? '#6366f1' : '#f59e0b'
              }}
            >
              {actionType === 'call' && <Call fontSize="inherit" />}
              {actionType === 'email' && <Mail fontSize="inherit" />}
              {actionType === 'map' && <Map fontSize="inherit" />}
            </IconButton>
          )}
        </>
      )}
    </Box>
  </Box>
);

export default CarrierCard;
